import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback} from 'react';
import {useReactFlow, useViewport} from 'reactflow';

import BaseControlButton from './BaseControlButton';

export const MAX_ZOOM = 2;
export const MIN_ZOOM = 0.1;
const ZOOM_INCREMENT = 0.1;
export const ZOOM_ANIMATION_DURATION = 0; // in milliseconds

interface HorizontalControlsProps {
  /**
   * The number of pixels to shift the controls.
   *
   * This is useful for when the controls will be hidden behind a separate element,
   * for example, a FlowchartSidebar + FlowchartContextPanel combo that hides
   * a portion of the canvas.
   */
  shiftLeft?: number;
  additionalControls?: React.ReactNode;
}

/**
 * Customized ReactFlow controls for zooming and fitting the view.
 * This component is designed to be used within a ReactFlow component.
 *
 * Horizontal version of <Controls/> from reactflow, can be used in the same way.
 *
 * IMPORTANT: Set minZoom={MIN_ZOOM} and maxZoom={MAX_ZOOM} on the ReactFlow component to ensure
 * the zoom buttons work correctly.
 *
 * @example
 * <ReactFlow
 *   nodes={nodes}
 *   edges={edges}
 *   minZoom={MIN_ZOOM}
 *   maxZoom={MAX_ZOOM}
 * />
 *   <HorizontalControls />
 * </ReactFlow>
 *
 * @example
 * <ReactFlow
 *   nodes={nodes}
 *   edges={edges}
 *   minZoom={MIN_ZOOM}
 *   maxZoom={MAX_ZOOM}
 * >
 *   <HorizontalControls shiftLeft={40} />
 * </ReactFlow>
 */
export default function HorizontalControls(props: HorizontalControlsProps): JSX.Element {
  const {fitView, zoomTo} = useReactFlow();
  const {zoom: zoomLevel} = useViewport();

  const handleZoomIn = useCallback(() => {
    const newZoomLevel = Math.min(MAX_ZOOM, zoomLevel + ZOOM_INCREMENT);
    zoomTo(newZoomLevel, {duration: ZOOM_ANIMATION_DURATION});
  }, [zoomLevel, zoomTo]);

  const handleZoomOut = useCallback(() => {
    const newZoomLevel = Math.max(0, zoomLevel - ZOOM_INCREMENT);
    zoomTo(newZoomLevel, {duration: ZOOM_ANIMATION_DURATION});
  }, [zoomLevel, zoomTo]);

  const shiftLeft = props.shiftLeft ?? 0;

  return (
    <div
      className={classNames('nodrag nopan', css(HORIZONTAL_CONTROLS_STYLES.controls))}
      style={{left: shiftLeft}}
      role="region"
      aria-label="horizontal controls"
    >
      <div className={css(HORIZONTAL_CONTROLS_STYLES.zoomButtons)}>
        <BaseControlButton
          icon="minus"
          tooltip="Zoom out"
          onClick={() => {
            handleZoomOut();
          }}
          disabled={zoomLevel <= MIN_ZOOM}
        />
        <div className={css(HORIZONTAL_CONTROLS_STYLES.zoomDisplay)}>{`${Math.round(zoomLevel * 100)}%`}</div>
        <BaseControlButton
          icon="plus"
          tooltip="Zoom in"
          onClick={() => {
            handleZoomIn();
          }}
          disabled={zoomLevel >= MAX_ZOOM}
        />
      </div>
      <BaseControlButton
        icon="expand"
        tooltip="Fit view"
        onClick={() => {
          fitView({duration: ZOOM_ANIMATION_DURATION});
        }}
      />
      {props.additionalControls}
    </div>
  );
}

export const HORIZONTAL_CONTROLS_STYLES = StyleSheet.create({
  controls: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'absolute',
    bottom: 0,
    zIndex: 1000,
    margin: '15px',
    boxShadow: '0 0 2px 1px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(224, 224, 224, 0.69)',
    borderRadius: '4px',
    background: 'white',
  },
  zoomButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRight: '1px solid rgba(224, 224, 224, 0.69)',
  },
  zoomDisplay: {
    margin: '2px',
    width: '31px',
    textAlign: 'center',
  },
});
